<template>
  <!-- Público: mt-12 + py-12 deixa ~16px de ar abaixo da navbar fixa (h-20). Logado: pt-6 alinha melhor com a Navbar do app. -->
  <div
    class="w-full bg-gray-50"
    :class="tenantMode ? 'pt-6 pb-12' : 'mt-12 py-12'"
  >
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header: tamanhos base / sm / md (incrementados) -->
      <div v-if="showHeader" class="mb-6 md:mb-12">
        <h4 class="text-xl font-bold leading-tight tracking-tight text-gray-900 mb-2 sm:text-5xl sm:tracking-normal sm:mb-3 sm:leading-snug md:text-6xl md:mb-4 md:leading-normal">
          {{ tenantMode ? 'Analise seus imóveis' : 'Encontre Seu Próximo Destino' }}
        </h4>
        <p class="text-sm text-gray-600 mx-auto md:text-lg">
          {{ tenantMode
            ? 'Gerencie e todos os seus imóveis cadastrados no sistema.'
            : 'Explore nossa vitrine de imóveis para temporada.'
          }}
        </p>
      </div>

      <!-- Carregando vitrine de um tenant (evita mostrar filtros antes de saber se está ativo) -->
      <div v-if="tenantCode && !tenantMode && loading && !tenantInfo" class="mb-8 bg-white rounded-xl shadow-md p-8 text-center">
        <div class="animate-spin rounded-full h-10 w-10 border-4 border-indigo-200 border-t-indigo-600 mx-auto mb-4"></div>
        <p class="text-gray-600">Carregando...</p>
      </div>

      <!-- Tenant inativo: pendente de homologação -->
      <div v-else-if="tenantInfo && tenantInfo.active === false && !tenantMode" class="mb-8 bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg">
        <h3 class="text-lg font-semibold text-amber-900 mb-2">Ambiente pendente de homologação</h3>
        <p class="text-amber-800">
          O ambiente <strong>{{ tenantInfo.name }}</strong> ({{ tenantInfo.master_code }}) está em análise. Os imóveis ainda não estão visíveis na vitrine.
          <span v-if="!tenantInfo.rejection_message">Em breve analisaremos seu cadastro.</span>
        </p>
        <p v-if="tenantInfo.rejection_message" class="mt-3 text-amber-900 font-medium">
          {{ tenantInfo.rejection_message }}
        </p>
      </div>

      <div v-else-if="tenantInfo && !tenantMode" class="mb-8 bg-indigo-50 border-l-4 border-indigo-600 p-4 sm:p-6 rounded-lg">
        <div class="flex flex-col sm:flex-row sm:items-center gap-4">
          <div class="flex items-center gap-3 sm:gap-4 min-w-0">
          <img
            v-if="tenantInfo.logo_url"
            :src="tenantInfo.logo_url"
            :alt="tenantInfo.name"
            class="h-12 w-12 sm:h-14 sm:w-14 flex-shrink-0 rounded-full object-cover border border-gray-200 bg-white"
          />
          <svg v-else class="w-6 h-6 text-indigo-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          <div class="flex-1 min-w-0">
            <h3 class="text-xl sm:text-lg font-semibold text-indigo-900 break-words"> {{ tenantInfo.name }}</h3>
            <p class="text-base sm:text-sm text-indigo-700">Mostrando apenas os imóveis deste Anfitrião</p>
          </div>
          </div>
          <a
            v-if="tenantWhatsAppLink"
            :href="tenantWhatsAppLink"
            target="_blank"
            rel="noopener"
            class="inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold px-4 py-2 rounded-lg transition-colors w-full sm:w-auto flex-shrink-0"
          >
            <i class="fab fa-whatsapp text-lg"></i>
            <span>Contato</span>
          </a>
        </div>
      </div>

      <!-- Filters Section -->
      <div v-if="showFiltersAndList" class="mb-8 bg-white rounded-xl shadow-md p-4 md:p-6">
        <!-- Mobile: só cidade + botão estilo Airbnb para expandir demais filtros -->
        <div class="flex md:hidden items-stretch gap-2">
          <div
            class="mobile-city-pill flex-1 min-w-0 rounded-full border-2 border-gray-900 bg-white px-1 py-0.5 shadow-sm"
            aria-label="Cidade"
          >
            <CitySelectAsync
              v-model="filters.city_id"
              :selected-label="filters.city_label"
              label=""
              clearable
              placeholder="Digite para buscar cidade..."
              @update:model-value="onShowcaseCityFilterChange"
              @city-picked="onShowcaseCityPicked"
            />
          </div>
          <button
            type="button"
            class="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full border-2 border-gray-900 bg-white text-gray-900 shadow-sm hover:bg-gray-50 transition-colors"
            :class="{ 'ring-2 ring-indigo-500 ring-offset-2': showMoreFiltersMobile || hasActiveAdvancedFilters }"
            :aria-expanded="showMoreFiltersMobile"
            aria-controls="vitrine-filters-more-mobile"
            aria-label="Mais filtros"
            @click="showMoreFiltersMobile = !showMoreFiltersMobile"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
              />
            </svg>
          </button>
        </div>

        <!-- Mobile: filtros extras (expandido) -->
        <div
          v-show="showMoreFiltersMobile"
          id="vitrine-filters-more-mobile"
          class="md:hidden mt-4 pt-4 border-t border-gray-200 space-y-4"
        >
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">Cidade</label>
            <CitySelectAsync
              v-model="filters.city_id"
              :selected-label="filters.city_label"
              label=""
              clearable
              placeholder="Digite para buscar cidade..."
              @update:model-value="onShowcaseCityFilterChange"
              @city-picked="onShowcaseCityPicked"
            />
          </div>
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">Nome ou descrição do imóvel</label>
            <div class="relative">
              <input
                v-model="filters.search"
                @input="onSearchInput"
                type="text"
                placeholder="Nome ou descrição..."
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
              />
              <svg class="absolute right-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          <div>
            <p class="text-sm font-semibold text-gray-700 mb-2">Quando deseja se hospedar?</p>
            <div class="grid grid-cols-2 gap-2">
              <div>
                <label class="block text-xs text-gray-600 mb-1">Data entrada</label>
                <input
                  v-model="filters.available_from"
                  type="date"
                  :max="filters.available_to || undefined"
                  class="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                  @change="onAvailabilityDateChange"
                >
              </div>
              <div>
                <label class="block text-xs text-gray-600 mb-1">Data Saída</label>
                <input
                  v-model="filters.available_to"
                  type="date"
                  :min="filters.available_from || undefined"
                  class="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                  @change="onAvailabilityDateChange"
                >
              </div>
            </div>
          </div>
          <div class="grid grid-cols-1 gap-4">
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">Quartos</label>
              <select
                v-model="filters.bedrooms"
                @change="triggerSearch"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
              >
                <option value="">Qualquer quantidade</option>
                <option value="1">1+ quartos</option>
                <option value="2">2+ quartos</option>
                <option value="3">3+ quartos</option>
                <option value="4">4+ quartos</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">Hóspedes</label>
              <select
                v-model="filters.guests"
                @change="triggerSearch"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
              >
                <option value="">Qualquer número</option>
                <option value="1">1+ hóspede</option>
                <option value="2">2+ hóspedes</option>
                <option value="4">4+ hóspedes</option>
                <option value="6">6+ hóspedes</option>
              </select>
            </div>
          </div>
          <div class="pt-2 border-t border-gray-100">
            <p class="text-sm font-semibold text-gray-700 mb-3">Comodidades</p>
            <div class="grid grid-cols-2 gap-3">
              <label class="flex items-center gap-2 cursor-pointer">
                <input v-model="filters.wifi" type="checkbox" class="rounded" @change="triggerSearch">
                <span class="text-sm text-gray-700">WiFi</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input v-model="filters.air_conditioning" type="checkbox" class="rounded" @change="triggerSearch">
                <span class="text-sm text-gray-700">Ar Condicionado</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input v-model="filters.pool" type="checkbox" class="rounded" @change="triggerSearch">
                <span class="text-sm text-gray-700">Piscina</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input v-model="filters.parking" type="checkbox" class="rounded" @change="triggerSearch">
                <span class="text-sm text-gray-700">Estacionamento</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input v-model="filters.pet_friendly" type="checkbox" class="rounded" @change="triggerSearch">
                <span class="text-sm text-gray-700">Pet Friendly</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input v-model="filters.kitchen" type="checkbox" class="rounded" @change="triggerSearch">
                <span class="text-sm text-gray-700">Cozinha</span>
              </label>
            </div>
          </div>
          <div class="flex flex-col gap-2 pt-2">
            <button
              type="button"
              @click="resetFilters"
              class="w-full px-6 py-2 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
            >
              Limpar Filtros
            </button>
            <button
              type="button"
              @click="applyFilters"
              class="w-full px-6 py-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-indigo-800 transition-all"
            >
              Buscar
            </button>
          </div>
        </div>

        <!-- Desktop / tablet: todos os filtros visíveis -->
        <div class="hidden md:block">
          <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">Cidade</label>
              <CitySelectAsync
                v-model="filters.city_id"
                :selected-label="filters.city_label"
                label=""
                clearable
                placeholder="Digite para buscar cidade..."
                @update:model-value="onShowcaseCityFilterChange"
                @city-picked="onShowcaseCityPicked"
              />
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">Nome ou descrição do imóvel</label>
              <div class="relative">
                <input
                  v-model="filters.search"
                  @input="onSearchInput"
                  type="text"
                  placeholder="Nome ou descrição..."
                  class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                />
                <svg class="absolute right-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">Quartos</label>
              <select
                v-model="filters.bedrooms"
                @change="triggerSearch"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
              >
                <option value="">Qualquer quantidade</option>
                <option value="1">1+ quartos</option>
                <option value="2">2+ quartos</option>
                <option value="3">3+ quartos</option>
                <option value="4">4+ quartos</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">Hóspedes</label>
              <select
                v-model="filters.guests"
                @change="triggerSearch"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
              >
                <option value="">Qualquer número</option>
                <option value="1">1+ hóspede</option>
                <option value="2">2+ hóspedes</option>
                <option value="4">4+ hóspedes</option>
                <option value="6">6+ hóspedes</option>
              </select>
            </div>
          </div>

          <div class="mt-4 pt-4 border-t border-gray-200">
            <p class="text-sm font-semibold text-gray-700 mb-3">Comodidades</p>
            <div class="grid grid-cols-2 md:grid-cols-6 gap-3">
              <label class="flex items-center gap-2 cursor-pointer">
                <input v-model="filters.wifi" type="checkbox" class="rounded" @change="triggerSearch">
                <span class="text-sm text-gray-700">WiFi</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input v-model="filters.air_conditioning" type="checkbox" class="rounded" @change="triggerSearch">
                <span class="text-sm text-gray-700">Ar Condicionado</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input v-model="filters.pool" type="checkbox" class="rounded" @change="triggerSearch">
                <span class="text-sm text-gray-700">Piscina</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input v-model="filters.parking" type="checkbox" class="rounded" @change="triggerSearch">
                <span class="text-sm text-gray-700">Estacionamento</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input v-model="filters.pet_friendly" type="checkbox" class="rounded" @change="triggerSearch">
                <span class="text-sm text-gray-700">Pet Friendly</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input v-model="filters.kitchen" type="checkbox" class="rounded" @change="triggerSearch">
                <span class="text-sm text-gray-700">Cozinha</span>
              </label>
            </div>
          </div>

          <!-- Mesma grade 4 colunas da linha Nome/Cidade/Quartos/Hóspedes: datas alinhadas + botões à direita -->
          <div class="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div class="min-w-0">
              <label class="block text-sm font-semibold text-gray-700 mb-2">Data Entrada</label>
              <input
                v-model="filters.available_from"
                type="date"
                :max="filters.available_to || undefined"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                @change="onAvailabilityDateChange"
              >
            </div>
            <div class="min-w-0">
              <label class="block text-sm font-semibold text-gray-700 mb-2">Data Saída</label>
              <input
                v-model="filters.available_to"
                type="date"
                :min="filters.available_from || undefined"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                @change="onAvailabilityDateChange"
              >
            </div>
            <div class="md:col-span-2 flex flex-wrap justify-start md:justify-end gap-3 pb-0.5">
              <button
                type="button"
                @click="resetFilters"
                class="px-6 py-2 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                Limpar Filtros
              </button>
              <button
                type="button"
                @click="applyFilters"
                class="px-6 py-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-indigo-800 transition-all whitespace-nowrap"
              >
                Buscar
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- No Results -->
      <div v-if="showFiltersAndList && !loading && filteredProperties.length === 0" class="text-center py-12">
        <svg class="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
        </svg>
        <h3 class="text-lg font-semibold text-gray-900 mb-2">Nenhum imóvel encontrado</h3>
        <p class="text-gray-600 mb-6">Ajuste os filtros ou tente uma nova busca</p>
      </div>

      <!-- Properties Grid -->
      <div v-else-if="showFiltersAndList" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <PropertyCard 
          v-for="property in filteredProperties"
          :key="property.id"
          :property="property"
          @view-details="viewPropertyDetails(property)"
        />
      </div>

      <!-- Load More Button -->
      <div v-if="showFiltersAndList && hasMoreProperties" class="mt-12 text-center">
        <button 
          @click="loadMore"
          class="px-8 py-3 border-2 border-indigo-600 text-indigo-600 font-bold rounded-lg hover:bg-indigo-50 transition-colors"
        >
          Carregar Mais
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import PropertyCard from './PropertyCard.vue'
import CitySelectAsync from './CitySelectAsync.vue'
import { navigateTo, RESERVED_FIRST_SEGMENTS } from '../router'
import axios from 'axios'
import { useBrazilianMasks } from '../composables/useBrazilianMasks.js'
import { getWhatsAppShareHref } from '../composables/useWhatsAppShare.js'

export default {
  name: 'PropertyShowcase',
  components: { PropertyCard, CitySelectAsync },
  props: {
    tenantMode: {
      type: Boolean,
      default: false
    },
    showHeader: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      properties: [],
      loading: false,
      tenantInfo: null,
      tenantCode: null,
      filters: {
        search: '',
        city_id: '',
        city_label: '',
        bedrooms: '',
        guests: '',
        available_from: '',
        available_to: '',
        wifi: false,
        air_conditioning: false,
        pool: false,
        parking: false,
        pet_friendly: false,
        kitchen: false
      },
      currentPage: 1,
      lastPage: 1,
      searchDebounceId: null,
      showMoreFiltersMobile: false
    }
  },

  computed: {
    /** Indica se há filtros além da cidade (destaque no botão de filtros no mobile) */
    hasActiveAdvancedFilters() {
      const f = this.filters
      return !!(
        (f.search && f.search.trim().length >= 3) ||
        (f.available_from && f.available_to) ||
        (f.city_id !== '' && f.city_id != null) ||
        f.bedrooms ||
        f.guests ||
        f.wifi ||
        f.air_conditioning ||
        f.pool ||
        f.parking ||
        f.pet_friendly ||
        f.kitchen
      )
    },
    tenantWhatsAppLink() {
      const phone = this.tenantInfo?.phone
      if (!phone) return null
      const { getWhatsAppLink } = useBrazilianMasks()
      if (!getWhatsAppLink(phone)) return null
      const message =
        'Olá, gostaria de obter mais informações sobre os seus imóveis para locação!'
      return getWhatsAppShareHref(phone, message)
    },

    filteredProperties() {
      return this.properties
    },

    hasMoreProperties() {
      return this.currentPage < this.lastPage
    },

    // Esconde filtros e lista enquanto carrega a página de um tenant ou quando o tenant está inativo
    showFiltersAndList() {
      if (this.tenantMode) return true
      if (!this.tenantCode) return true
      if (this.loading && !this.tenantInfo) return false
      if (this.tenantInfo && this.tenantInfo.active === false) return false
      return true
    }
  },

  methods: {
    onShowcaseCityPicked({ label }) {
      this.filters.city_label = label || ''
    },

    onShowcaseCityFilterChange() {
      if (this.filters.city_id === '' || this.filters.city_id == null) {
        this.filters.city_label = ''
      }
      this.triggerSearch()
    },

    triggerSearch() {
      this.currentPage = 1
      this.loadProperties(1, false)
    },

    /** Só dispara busca automática quando início e fim estão preenchidos; senão usa "Buscar". */
    onAvailabilityDateChange() {
      if (this.filters.available_from && this.filters.available_to) {
        this.triggerSearch()
      }
    },

    onSearchInput() {
      if (this.searchDebounceId) {
        clearTimeout(this.searchDebounceId)
      }

      const term = (this.filters.search || '').trim()

      // Só busca se vazio (limpar) ou com 3+ caracteres
      if (term !== '' && term.length < 3) {
        return
      }

      this.searchDebounceId = setTimeout(() => {
        this.triggerSearch()
      }, 400)
    },
    async loadProperties(page = 1, append = false) {
      try {
        this.loading = true
        let data
        let tenantInfo = null
        let pagy = null

        if (this.tenantMode) {
          const userToken = localStorage.getItem('userToken')
          const tenantToken = localStorage.getItem('tenantToken')
          const response = await axios.get('/api/v1/properties', {
            headers: {
              'Authorization': `Bearer ${userToken}`,
              'Tenant-Authorization': `Bearer ${tenantToken}`
            },
            params: {
              page,
              search: this.filters.search || undefined,
              city_id: this.filters.city_id || undefined,
              bedrooms_min: this.filters.bedrooms || undefined,
              guests_min: this.filters.guests || undefined,
              wifi: this.filters.wifi || undefined,
              air_conditioning: this.filters.air_conditioning || undefined,
              pool: this.filters.pool || undefined,
              parking: this.filters.parking || undefined,
              pet_friendly: this.filters.pet_friendly || undefined,
              kitchen: this.filters.kitchen || undefined,
              available_from: this.filters.available_from || undefined,
              available_to: this.filters.available_to || undefined
            }
          })
          const payload = response.data || {}
          data = payload.data || []
          pagy = payload.pagy || null
        } else {
          const params = new URLSearchParams()
          if (this.tenantCode) params.append('tenant_code', this.tenantCode)
          params.append('page', page)

          // Filtros para a vitrine pública (aplicados somente quando o utilizador clica em "Buscar" ou "Limpar filtros")
          if (this.filters.search) params.append('search', this.filters.search)
          if (this.filters.city_id !== '' && this.filters.city_id != null) {
            params.append('city_id', String(this.filters.city_id))
          }
          if (this.filters.bedrooms) params.append('bedrooms_min', this.filters.bedrooms)
          if (this.filters.guests) params.append('guests_min', this.filters.guests)
          if (this.filters.wifi) params.append('wifi', 'true')
          if (this.filters.air_conditioning) params.append('air_conditioning', 'true')
          if (this.filters.pool) params.append('pool', 'true')
          if (this.filters.parking) params.append('parking', 'true')
          if (this.filters.pet_friendly) params.append('pet_friendly', 'true')
          if (this.filters.kitchen) params.append('kitchen', 'true')
          if (this.filters.available_from) params.append('available_from', this.filters.available_from)
          if (this.filters.available_to) params.append('available_to', this.filters.available_to)

          const response = await fetch(`/api/v1/properties/public/showcase?${params.toString()}`)
          
          if (!response.ok) {
            throw new Error('Erro ao carregar imóveis')
          }
          const result = await response.json()
          data = result.properties || []
          tenantInfo = result.tenant || null
          pagy = result.pagy || null
        }

        if (append) {
          this.properties = this.properties.concat(data)
        } else {
          this.properties = data
        }

        if (pagy) {
          this.currentPage = pagy.page || page
          this.lastPage = pagy.last || pagy.pages || this.currentPage
        } else {
          this.currentPage = page
          this.lastPage = page
        }

        this.tenantInfo = tenantInfo
      } catch (err) {
        console.error('Error loading properties:', err)
      } finally {
        this.loading = false
      }
    },

    applyFilters() {
      // Botão "Buscar" força uma nova busca com os filtros atuais
      this.triggerSearch()
      this.showMoreFiltersMobile = false
    },

    resetFilters() {
      this.filters = {
        search: '',
        city_id: '',
        city_label: '',
        bedrooms: '',
        guests: '',
        available_from: '',
        available_to: '',
        wifi: false,
        air_conditioning: false,
        pool: false,
        parking: false,
        pet_friendly: false,
        kitchen: false
      }
      // Carrega a primeira página sem filtros (comportamento de "ver todos")
      this.currentPage = 1
      this.loadProperties(1, false)
      this.showMoreFiltersMobile = false
    },

    loadMore() {
      if (this.hasMoreProperties) {
        this.loadProperties(this.currentPage + 1, true)
      }
    },

    viewPropertyDetails(property) {
      navigateTo('property-detail', { id: property.id })
    }
  },

  mounted() {
    if (!this.tenantMode) {
      const segments = window.location.pathname.split('/').filter(Boolean)
      if (segments.length > 0) {
        const first = segments[0]
        if (!RESERVED_FIRST_SEGMENTS.has(first.toLowerCase())) {
          this.tenantCode = first
        }
      }
    }
    // Ao entrar na tela, carregar a primeira página como se tivesse clicado em "Limpar filtros"
    this.resetFilters()
  }
}
</script>

<style scoped>
/* Pill da cidade no mobile: input interno sem borda duplicada */
.mobile-city-pill :deep(input) {
  border: none;
  box-shadow: none;
  border-radius: 9999px;
  background: transparent;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}
.mobile-city-pill :deep(input:focus) {
  outline: none;
  box-shadow: 0 0 0 2px rgb(79 70 229 / 0.5);
}
</style>
